import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { contractAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  FileText, 
  Search, 
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const Contracts = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsRes, employeesRes] = await Promise.all([
        contractAPI.getAll(),
        employeeAPI.getAll()
      ]);
      setContracts(contractsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Contracts fetch error:', error);
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeInfo = (employeeId) => {
    return employees.find(e => e.employee_id === employeeId);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Expired</Badge>;
      case 'terminated':
        return <Badge className="bg-slate-100 text-slate-600">Terminated</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'permanent': return 'bg-emerald-100 text-emerald-700';
      case 'fixed_term': return 'bg-blue-100 text-blue-700';
      case 'contractor': return 'bg-violet-100 text-violet-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Generate demo contracts if none exist
  const displayContracts = contracts.length > 0 ? contracts : employees.slice(0, 10).map((emp, idx) => ({
    id: `contract-${idx}`,
    employee_id: emp.employee_id,
    contract_type: ['permanent', 'fixed_term', 'contractor'][idx % 3],
    start_date: `202${3 + (idx % 3)}-0${(idx % 9) + 1}-01`,
    end_date: idx % 3 === 1 ? `2026-12-31` : '',
    salary: emp.salary,
    benefits: ['Medical Aid', 'Pension', 'Leave'],
    probation_end: `202${3 + (idx % 3)}-0${(idx % 9) + 3}-01`,
    status: idx % 5 === 0 ? 'expired' : 'active',
    signed_date: `202${3 + (idx % 3)}-0${(idx % 9) + 1}-05`
  }));

  const filteredContracts = displayContracts.filter(contract => {
    const emp = getEmployeeInfo(contract.employee_id);
    const name = `${emp?.first_name} ${emp?.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || 
           contract.employee_id?.includes(searchQuery);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading contracts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Contract Management
              </h2>
              <p className="text-violet-100">
                View and manage employee contracts
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Active Contracts</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {filteredContracts.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Permanent</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {filteredContracts.filter(c => c.contract_type === 'permanent').length}
                </p>
              </div>
              <FileText className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Fixed Term</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {filteredContracts.filter(c => c.contract_type === 'fixed_term').length}
                </p>
              </div>
              <Calendar className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm">Expiring Soon</p>
                <p className="text-3xl font-bold text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {filteredContracts.filter(c => c.status === 'expired').length}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by employee name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="contracts-search"
        />
      </div>

      {/* Contracts List */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Employee Contracts</CardTitle>
          <CardDescription>All employment contracts and agreements</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredContracts.map((contract) => {
              const emp = getEmployeeInfo(contract.employee_id);
              return (
                <div 
                  key={contract.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    contract.status === 'expired' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'
                  }`}
                  data-testid={`contract-item-${contract.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={emp?.avatar} />
                      <AvatarFallback className="bg-violet-100 text-violet-600 font-medium">
                        {emp?.first_name?.[0]}{emp?.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {emp?.first_name} {emp?.last_name}
                      </p>
                      <p className="text-sm text-slate-500">{emp?.position}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getTypeColor(contract.contract_type)}>
                          {contract.contract_type?.replace('_', ' ')}
                        </Badge>
                        {getStatusBadge(contract.status)}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                      <Calendar className="w-4 h-4" />
                      Start: {contract.start_date}
                    </div>
                    {contract.end_date && (
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Calendar className="w-4 h-4" />
                        End: {contract.end_date}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                      <DollarSign className="w-4 h-4" />
                      R {contract.salary?.toLocaleString()}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredContracts.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Contracts Found</h3>
              <p className="text-slate-500">Try adjusting your search</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Contracts;
